/**
 * Preferences Store (Zustand with persistence)
 * 
 * Stores user preferences that persist across sessions.
 * Uses localStorage for simple persistence.
 * 
 * @see docs/Research/DesignSpecsClaude.md Section 6.3.2
 */

import { create } from 'zustand';
import { createJSONStorage,persist } from 'zustand/middleware';

import { DEFAULT_PREFERENCES, type IUserPreferences } from '@/interfaces/IStorage';

interface PreferencesState extends IUserPreferences {
  // UI State
  sidebarWidth: number;
  
  // Actions
  setTheme: (theme: IUserPreferences['theme']) => void;
  setFontSize: (fontSize: IUserPreferences['fontSize']) => void;
  setAutoRunPipeline: (enabled: boolean) => void;
  setAutoCopyOutputToClipboard: (enabled: boolean) => void;
  setShowIntermediateResults: (show: boolean) => void;
  setShowIoStats: (show: boolean) => void;
  setShowToolNameWithStepLabel: (show: boolean) => void;
  setDefaultIndentSize: (size: number) => void;
  setEnableLocalTelemetry: (enabled: boolean) => void;
  setAutoVisualizers: (enabled: boolean) => void;
  setShowVisualizersInPipeline: (value: boolean) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setLastOpenedPipelineId: (id: string | undefined) => void;
  setSidebarWidth: (width: number) => void;
  toggleSidebar: () => void;
  resetToDefaults: () => void;
}

/**
 * Preferences store with localStorage persistence.
 */
export const usePreferencesStore = create<PreferencesState>()(
  persist(
    (set, get) => ({
      // Initial state from defaults
      ...DEFAULT_PREFERENCES,
      sidebarWidth: 280,
      
      // Actions
      setTheme: (theme) => set({ theme }),
      setFontSize: (fontSize) => set({ fontSize }),
      setAutoRunPipeline: (autoRunPipeline) => set({ autoRunPipeline }),
      setAutoCopyOutputToClipboard: (autoCopyOutputToClipboard) => set({ autoCopyOutputToClipboard }),
      setShowIntermediateResults: (showIntermediateResults) => set({ showIntermediateResults }),
      setShowIoStats: (showIoStats) => set({ showIoStats }),
      setShowToolNameWithStepLabel: (showToolNameWithStepLabel) => set({ showToolNameWithStepLabel }),
      setDefaultIndentSize: (defaultIndentSize) => set({ defaultIndentSize }),
      setEnableLocalTelemetry: (enableLocalTelemetry) => set({ enableLocalTelemetry }),
      setAutoVisualizers: (autoVisualizers) => set({ autoVisualizers }),
      setShowVisualizersInPipeline: (showVisualizersInPipeline) => set({ showVisualizersInPipeline }),
      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
      setLastOpenedPipelineId: (lastOpenedPipelineId) => set({ lastOpenedPipelineId }),
      setSidebarWidth: (sidebarWidth) => set({ sidebarWidth }),
      toggleSidebar: () => set({ sidebarCollapsed: !get().sidebarCollapsed }),
      resetToDefaults: () => set({ ...DEFAULT_PREFERENCES, sidebarWidth: 280 }),
    }),
    {
      name: 'swedevtools-preferences',
      version: 1,
      migrate: (persisted, _version) => {
        const defaults = { ...DEFAULT_PREFERENCES, sidebarWidth: 280 };
        return { ...defaults, ...(persisted as Record<string, unknown>) } as PreferencesState;
      },
      storage: createJSONStorage(() => localStorage),
    }
  )
);

/**
 * Hook to get the resolved theme (handles 'system' setting).
 */
export function useResolvedTheme(): 'light' | 'dark' {
  const theme = usePreferencesStore((state) => state.theme);
  
  if (theme === 'system') {
    // Check system preference
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  }
  
  return theme;
}

